import React, { createContext, useState, useEffect, useContext } from 'react';
import { createStorefrontApiClient } from '@shopify/storefront-api-client';

const client = createStorefrontApiClient({
  storeDomain: process.env.GATSBY_SHOPIFY_STORE_URL!,
  apiVersion: process.env.GATSBY_SHOPIFY_API_VERSION,
  publicAccessToken: process.env.GATSBY_SHOPIFY_STOREFRONT_ACCESS_KEY!,
});

const defaultValues = {
  cart: null,
  loading: false,
  didJustAddToCart: false,
  addVariantToCart: (variantId: string, quantity: number) => {},
  removeLineItem: (cartId: string, lineId: string) => {},
  updateLineItem: (cartId: string, lineId: string, quantity: number) => {},
  checkoutUrl: '',
};

const StoreContext = createContext(defaultValues);
const localStorageKey = `shopify_cart_id`;

export const StoreProvider = ({ children }) => {
  const [cart, setCart] = useState<any>(defaultValues.cart);
  const [loading, setLoading] = useState(false);
  const [didJustAddToCart, setDidJustAddToCart] = useState(false);
  const isBrowser = typeof window !== "undefined";

  const setCartItem = (cart: any) => {
    if (isBrowser) localStorage.setItem(localStorageKey, cart.id);
    setCart(cart);
  };

  const fetchCart = async (cartId: string) => {
    const { data } = await client.request(
      `
      query getCart($cartId: ID!) {
        cart(id: $cartId) {
          id
          checkoutUrl
          cost {
            subtotalAmount {
              amount
              currencyCode
            }
            totalAmount {
              amount
              currencyCode
            }
            totalTaxAmount {
              amount
              currencyCode
            }
          }
          lines(first: 50) {
            edges {
              node {
                id
                quantity
                merchandise {
                  ... on ProductVariant {
                    id
                    title
                    image {
                      url
                      altText
                    }
                    product {
                      title
                    }
                    priceV2 {
                      amount
                      currencyCode
                    }
                  }
                }
              }
            }
          }
        }
      }
    `,
      { variables: { cartId } }
    );
    return data.cart;
  };

  useEffect(() => {
    const initializeCart = async () => {
      const existingCartId = isBrowser
        ? localStorage.getItem(localStorageKey)
        : null;
      if (existingCartId) {
        try {
          const existingCart = await fetchCart(existingCartId);
          if (existingCart) {
            setCartItem(existingCart);
            return;
          }
        } catch (e) {
          if (isBrowser) localStorage.setItem(localStorageKey, "");
        }
      }

      const { data } = await client.request(`
        mutation {
          cartCreate {
            cart {
              id
              checkoutUrl
              cost {
                subtotalAmount {
                  amount
                  currencyCode
                }
                totalAmount {
                  amount
                  currencyCode
                }
                totalTaxAmount {
                  amount
                  currencyCode
                }
              }
            }
          }
        }
      `);
      setCartItem(data.cartCreate.cart);
    };
    initializeCart();
  }, []);

  const addVariantToCart = async (variantId: string, quantity: number) => {
    if (!cart?.id) return;
    setLoading(true);
    const { data } = await client.request(
      `
      mutation addToCart($cartId: ID!, $variantId: ID!, $quantity: Int!) {
        cartLinesAdd(
          cartId: $cartId
          lines: [{ quantity: $quantity, merchandiseId: $variantId }]
        ) {
          cart {
            id
            checkoutUrl
            cost {
              subtotalAmount {
                amount
                currencyCode
              }
              totalAmount {
                amount
                currencyCode
              }
              totalTaxAmount {
                amount
                currencyCode
              }
            }
            lines(first: 50) {
              edges {
                node {
                  id
                  quantity
                  merchandise {
                    ... on ProductVariant {
                      id
                      title
                      image {
                        url
                        altText
                      }
                      product {
                        title
                      }
                      priceV2 {
                        amount
                        currencyCode
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    `,
      { variables: { cartId: cart.id, variantId, quantity } }
    );
    setCart(data.cartLinesAdd.cart);
    setLoading(false);
    setDidJustAddToCart(true);
    setTimeout(() => setDidJustAddToCart(false), 3000);
  };

  const removeLineItem = async (cartId: string, lineId: string) => {
    setLoading(true);
    const { data } = await client.request(
      `
      mutation removeLine($cartId: ID!, $lineIds: [ID!]!) {
        cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
          cart {
            id
            checkoutUrl
            cost {
              subtotalAmount {
                amount
                currencyCode
              }
              totalAmount {
                amount
                currencyCode
              }
              totalTaxAmount {
                amount
                currencyCode
              }
            }
            lines(first: 50) {
              edges {
                node {
                  id
                  quantity
                  merchandise {
                    ... on ProductVariant {
                      id
                      title
                      image {
                        url
                        altText
                      }
                      product {
                        title
                      }
                      priceV2 {
                        amount
                        currencyCode
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    `,
      { variables: { cartId, lineIds: [lineId] } }
    );
    setCart(data.cartLinesRemove.cart);
    setLoading(false);
  };

  const updateLineItem = async (
    cartId: string,
    lineId: string,
    quantity: number
  ) => {
    setLoading(true);
    const { data } = await client.request(
      `
      mutation updateLine($cartId: ID!, $lineId: ID!, $quantity: Int!) {
        cartLinesUpdate(
          cartId: $cartId
          lines: [{ id: $lineId, quantity: $quantity }]
        ) {
          cart {
            id
            checkoutUrl
            cost {
              subtotalAmount {
                amount
                currencyCode
              }
              totalAmount {
                amount
                currencyCode
              }
              totalTaxAmount {
                amount
                currencyCode
              }
            }
            lines(first: 50) {
              edges {
                node {
                  id
                  quantity
                  merchandise {
                    ... on ProductVariant {
                      id
                      title
                      image { url altText }
                      product { title }
                      priceV2 { amount currencyCode }
                    }
                  }
                }
              }
            }
          }
        }
      }
    `,
      { variables: { cartId, lineId, quantity } }
    );
    setCart(data.cartLinesUpdate.cart);
    setLoading(false);
  };

  return (
    <StoreContext.Provider
      value={{
        ...defaultValues,
        addVariantToCart,
        removeLineItem,
        updateLineItem,
        cart,
        checkoutUrl: cart?.checkoutUrl,
        loading,
        didJustAddToCart,
      }}
    >
      {children}
    </StoreContext.Provider>
  );
};

const useStore = () => {
  const context = useContext(StoreContext);
  if (context === undefined) {
    throw new Error("useStore must be used within StoreContext");
  }
  return context;
};

export default useStore;
